<script>
import { apiSecond } from '@/boot/axios'
import { NCALayerClientIns } from '@/shared/services/nca'

export default {
	name: 'AuthPage',
	components: {},
	data() {
		return {
			ecpSign: true,
			token: null,
			registration_agreement: false,
			message: {
				status: null,
				text: null,
				texts: null,
			},
			loading: null,
			dataEcp: '',
			password: '',
			inputType: false,
			toast: {
				show: false,
				text: '',
				color: 'error',
			},
		}
	},
	methods: {
		// Show toast notification
		showToast(text, color = 'error') {
			this.toast = {
				show: true,
				text,
				color,
			}
			// Auto-hide toast after 5 seconds
			setTimeout(() => {
				this.toast.show = false
			}, 5000)
		},

		// Handle NCALayer specific errors
		handleNCALayerError(error) {
			console.error('NCALayer error:', error)

			// Determine error type and show appropriate message
			if (error instanceof Event && error.type === 'error') {
				// WebSocket error
				this.showToast(this.$t('system_message.nca_websocket_error'))
			} else if (error instanceof Event && error.type === 'close') {
				// Connection closed
				this.showToast(this.$t('system_message.nca_connection_failed'))
			} else if (error.code === 500) {
				// NCALayer internal error
				this.showToast(this.$t('system_message.nca_certificate_error'))
			} else if (error.message && error.message.includes('timeout')) {
				// Timeout error - don't show error, let user continue
				console.log('NCALayer timeout occurred, allowing user to continue')
				// Don't reset loading state or show error for timeout
				return
			} else if (error.message && error.message.includes('connection')) {
				// Connection failed
				this.showToast(this.$t('system_message.nca_connection_failed'))
			} else {
				// Unknown error
				this.showToast(this.$t('system_message.nca_unknown_error'))
			}
		},

		catchError(error) {
			if (error?.response?.status == 500) {
				this.message.status = 500
				this.message.text = this.$t('system_message.500')
			}
			if (error?.response?.status == 401) {
				this.message.status = 401
				this.message.text = error.response.data.error_message
			}
			if (error?.response?.status == 422) {
				this.message.status = 422
				this.message.text = error.response.data.error_message
			}
		},

		onSubmit(evt) {
			if (!this.registration_agreement) return
			this.message.status = null
			this.message.text = null
			this.message.texts = null
			const formData = new FormData(evt.target)

			apiSecond
				.post('v1/login', formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
				.then((response) => {
					if (response.status == 200) {
						this.message.status = response.status
						this.message.text = response.data.message

						this.$store.dispatch('set_tokens', {
							access_token: response.data.access_token,
							refresh_token: response.data.refresh_token,
							token_time: new Date().getTime(),
						})

						this.token = response.data.access_token
						this.$router.push({ path: '/' + this.$i18n.locale + '/account' })
					}
				})
				.catch((error) => {
					this.catchError(error)
				})
		},

		async onPki() {
			try {
				// Clear previous messages
				this.message.status = null
				this.message.text = null
				this.message.texts = null

				// Show loading state
				this.loading = true

				const ncaData = await NCALayerClientIns.send()

				const response = await apiSecond
					.post('v1/login/pki', {
						data: ncaData,
					})
					.catch((error) => {
						this.catchError(error)
					})

				if (response?.status == 200) {
					this.message.status = response.status
					this.message.text = response.data.message

					this.$store.dispatch('set_tokens', {
						access_token: response.data.access_token,
						refresh_token: response.data.refresh_token,
						token_time: new Date().getTime(),
					})
					this.token = response.data.access_token
					this.$router.push({ path: '/' + this.$i18n.locale + '/account' })
				}
			} catch (error) {
				// Handle NCALayer specific errors
				this.handleNCALayerError(error)

				// Reset loading state for all errors so user can retry
				this.loading = false
			}
		},
	},
	head: {
		title() {
			return {
				inner: this.$t('site.authorization.title'),
			}
		},
		meta: function () {
			return [
				{
					name: 'description',
					content: this.$t('site.authorization.description'),
				},
			]
		},
	},
}
</script>
<template>
	<div class="wrap__content">
		<!-- Toast Notification -->
		<div
			v-if="toast.show"
			:class="[
				'toast-notification',
				`toast-${toast.color}`,
				'fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm',
			]"
		>
			<div class="flex items-center justify-between">
				<span class="text-white font-medium">{{ toast.text }}</span>
				<button
					@click="toast.show = false"
					class="ml-4 text-white hover:text-gray-200 focus:outline-none"
				>
					×
				</button>
			</div>
		</div>

		<div class="container">
			<div class="row">
				<div class="col-lg-4 offset-lg-4 pt-[10dvh]">
					<div class="login__block">
						<div class="content__title--block">
							<div class="content__title section__title">
								{{ $t('site.authorization.title') }}
							</div>
						</div>

						<div class="login__tabs">
							<div
								class="login__tab--item"
								v-bind:class="{ 'login__tab--active': ecpSign == false }"
							>
								<a @click="ecpSign = false">
									{{ $t('site.authorization.ecp') }}
								</a>
							</div>
							<div
								class="login__tab--item"
								v-bind:class="{ 'login__tab--active': ecpSign == true }"
							>
								<a @click="ecpSign = true">
									{{ $t('site.authorization.login') }}
								</a>
							</div>
						</div>

						<div class="login__block--form">
							<div class="row">
								<div class="col-lg-12">
									<div
										class="login__block--tab"
										v-bind:class="{ 'login__block--active': ecpSign == false }"
										v-if="ecpSign == false"
									>
										<label class="form__block--line">
											<div class="form__block--input">
												<div class="form__file" style="margin-bottom: 15px" @click="onPki">
													<img src="@/assets/img/icon-ecp.svg" />
													{{ $t('site.authorization.download_certificate') }}
													<!-- Loading indicator for ECP -->
													<div v-if="loading" class="ml-2 inline-block">
														<div
															class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"
														></div>
													</div>
												</div>

												<div class="form__line form__line--column">
													<div class="form__line--right">
														<div class="row">
															<div class="col-xl-12">
																<div class="registration_agreement">
																	<div class="registration_agreement__title">
																		{{ $t('register_agreement_title') }}
																	</div>
																	<p v-html="$t('register_agreement')" class="mb-4"></p>
																	<div
																		for="registration_agreement_input"
																		class="form__line--input registration_agreement_input"
																	>
																		<input
																			v-model="registration_agreement"
																			id="registration_agreement_input"
																			type="checkbox"
																			required
																			name="registration_agreement"
																		/>
																		<span>{{ $t('accept') }}</span>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
												<span class="input-required">Войти</span>
											</div>
										</label>
										<div
											class="form__block--line form__messages"
											v-if="message.status"
											:class="{
												'form__messages--error': message.status != 200,
												'form__messages--success': message.status == 200,
											}"
										>
											<label class="form__block--input">
												<span class="form__block--title" v-if="message.text">
													{{ $t(message.text) }}
												</span>
												<div
													class="form__block--title"
													v-if="message.texts && message.texts.password"
												>
													<p v-for="(text, textInd) in message.texts.password" :key="textInd">
														{{ text }}
													</p>
												</div>
											</label>
										</div>
									</div>
									<div
										class="login__block--tab"
										v-bind:class="{ 'login__block--active': ecpSign == true }"
										v-if="ecpSign == true"
									>
										<form method="POST" @submit.prevent="onSubmit">
											<div class="form__block--line">
												<label class="form__block--input">
													<input
														type="text"
														class="input-linck input-linck-icon"
														:class="[message.status != 200 && message.status ? 'input--error' : '']"
														required=""
														name="email"
														value=""
														:placeholder="$t('site.authorization.input.email_iin')"
													/>
													<div class="input__icon">
														<svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M15.471 16.647v-1.5a3 3 0 00-3-3h-6a3 3 0 00-3 3v1.5M9.471 9.147a3 3 0 100-6 3 3 0 000 6z" stroke="#52A5FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
													</div>
												</label>
											</div>
											<div class="form__block--line">
												<label class="form__block--input">
													<input
														type="password"
														v-if="!inputType"
														class="input-linck input-linck-icon"
														:class="[message.status != 200 && message.status ? 'input--error' : '']"
														v-model="password"
														required=""
														name="password"
														value=""
														:placeholder="$t('site.authorization.input.password')"
													/>
													<input
														type="text"
														v-else
														class="input-linck input-linck-icon"
														:class="[message.status != 200 && message.status ? 'input--error' : '']"
														v-model="password"
														required=""
														name="password"
														value=""
														:placeholder="$t('site.authorization.input.password')"
													/>
													<div class="input__icon">
														<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 1110 0v4" stroke="#52A5FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
													</div>
													<div
														class="password__eye"
														v-tooltip.top-center="{
															content: $t('site.authorization.input.password_length'),
															class: ['tooltip__btn'],
														}"
													>
														<img
															src="@/assets/img/icon-eye-off.svg"
															v-if="inputType"
															@click="inputType = false"
														/>
														<img src="@/assets/img/icon-eye.svg" v-else @click="inputType = true" />
													</div>
												</label>
											</div>

											<div
												class="form__block--line form__messages"
												v-if="message.status"
												:class="{
													'form__messages--error': message.status != 200,
													'form__messages--success': message.status == 200,
												}"
											>
												<label class="form__block--input">
													<span class="form__block--title" v-if="message.text">
														{{ $t(message.text) }}
													</span>
													<div
														class="form__block--title"
														v-if="message.texts && message.texts.password"
													>
														<p v-for="(text, textInd) in message.texts.password" :key="textInd">
															{{ text }}
														</p>
													</div>
												</label>
											</div>
											<div class="form__line form__line--column">
												<div class="form__line--right">
													<div class="row">
														<div class="col-xl-12">
															<div class="registration_agreement">
																<div class="registration_agreement__title">
																	{{ $t('register_agreement_title') }}
																</div>
																<p v-html="$t('register_agreement')" class="mb-4"></p>
																<label
																	for="registration_agreement_input"
																	class="form__line--input registration_agreement_input"
																>
																	<input
																		v-model="registration_agreement"
																		id="registration_agreement_input"
																		type="checkbox"
																		required
																		name="registration_agreement"
																	/>
																	<span>{{ $t('accept') }}</span>
																</label>
															</div>
														</div>
													</div>
												</div>
											</div>

											<div class="form__listitem--line form__listitem--button">
												<div class="form__listitem--input">
													<button class="input__button">
														{{ $t('site.authorization.input_login') }}
													</button>
												</div>
											</div>
										</form>
									</div>
								</div>

								<div class="col-lg-12">
									<div class="form__listitem--registr">
										<router-link :to="'/' + $i18n.locale + '/recovery'">{{
											$t('site.authorization.recovery')
										}}</router-link>
									</div>
								</div>
								<div class="col-lg-12">
									<div class="form__listitem--registr">
										<router-link :to="'/' + $i18n.locale + '/registration'">{{
											$t('site.authorization.registration')
										}}</router-link>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.registration_agreement {
	text-align: justify;
	font-size: 12px;
	line-height: 1.2;
}

.registration_agreement__title {
	text-align: center;
	font-weight: 600;
	margin-bottom: 10px;
}

.registration_agreement a {
	color: inherit;
	text-decoration: underline;
}

.registration_agreement_input {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;
	font-size: 14px;
	cursor: pointer;
}

.registration_agreement input {
	min-width: 22px;
	width: 22px;
	height: 22px;
	background: #ffffff;
	cursor: pointer;
}

/* Toast Notification Styles */
.toast-notification {
	animation: slideIn 0.3s ease-out;
}

.toast-error {
	background-color: #ef4444;
	border-left: 4px solid #dc2626;
}

.toast-success {
	background-color: #10b981;
	border-left: 4px solid #059669;
}

.toast-warning {
	background-color: #f59e0b;
	border-left: 4px solid #d97706;
}

.toast-info {
	background-color: #3b82f6;
	border-left: 4px solid #2563eb;
}

@keyframes slideIn {
	from {
		transform: translateX(100%);
		opacity: 0;
	}
	to {
		transform: translateX(0);
		opacity: 1;
	}
}

/* Loading spinner animation */
@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.animate-spin {
	animation: spin 1s linear infinite;
}

/* Retry button styles */
.retry-button {
	background-color: #3b82f6;
	color: white;
	border: none;
	padding: 8px 16px;
	border-radius: 6px;
	font-size: 14px;
	cursor: pointer;
	transition: background-color 0.2s;
}

.retry-button:hover:not(:disabled) {
	background-color: #2563eb;
}

.retry-button:disabled {
	background-color: #9ca3af;
	cursor: not-allowed;
}
</style>
